import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader } from "../components/Shared";
import { TableSkeleton } from "../components/Skeletons";
import { ShieldCheck, Check, Minus } from "lucide-react";

const ROLE_SHORT = {
  "CEO": "CEO",
  "Assistant CEO": "ACEO",
  "General Manager": "GM",
  "Financial Manager": "FM",
  "Site Supervisor": "Supervisor",
  "Employee": "Employee",
  "Intern": "Student",
};

export default function RoleAudit() {
  const { data, isLoading } = useQuery({ queryKey: ["role-audit"], queryFn: () => api.get("/role-audit").then((r) => r.data) });

  if (isLoading || !data) return <div data-testid="role-audit-page"><PageHeader title="Role Audit" subtitle="Who can do what across STEMS"><ShieldCheck className="h-6 w-6 text-[#1E3A8A]" /></PageHeader><TableSkeleton /></div>;

  const roles = data.roles || [];

  return (
    <div data-testid="role-audit-page">
      <PageHeader title="Role Audit" subtitle="Read-only overview of role permissions across STEMS">
        <ShieldCheck className="h-6 w-6 text-[#1E3A8A]" />
      </PageHeader>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="role-audit-matrix">
          <thead>
            <tr className="bg-[#1E3A8A] text-white">
              <th className="text-left font-semibold px-4 py-3 sticky left-0 bg-[#1E3A8A] min-w-[240px]">Capability</th>
              {roles.map((r) => (
                <th key={r} className="px-3 py-3 font-semibold text-center whitespace-nowrap" data-testid={`role-col-${r}`}>{ROLE_SHORT[r] || r}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.groups || []).map((g) => (
              <React.Fragment key={g.area}>
                <tr className="bg-slate-50">
                  <td colSpan={roles.length + 1} className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-500">{g.area}</td>
                </tr>
                {g.capabilities.map((c) => (
                  <tr key={c.action} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`cap-row-${c.action}`}>
                    <td className="px-4 py-2.5 text-slate-700 sticky left-0 bg-white">{c.action}</td>
                    {roles.map((r) => {
                      const allowed = (c.roles || []).includes(r);
                      return (
                        <td key={r} className="px-3 py-2.5 text-center">
                          {allowed
                            ? <Check className="h-4 w-4 text-emerald-600 inline" data-testid={`cap-yes-${c.action}-${r}`} />
                            : <Minus className="h-4 w-4 text-slate-300 inline" />}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400 mt-3">This matrix reflects the active server-side role permissions and is read-only.</p>
    </div>
  );
}
